// FLAGS: authorization, health-data, irreversible, silent-failure
// The session-note surface: reads, writes and deletes an expert's notes, note attachments and transcription summaries.
import {
  QueryObserverSuccessResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Consult } from "@src/models/Consult";
import { queryIdentifiers } from "@src/utils/constants";
import { Clients } from "@src/api/v1/Clients";
import {
  RequestConsultCreateRequest,
  RequestConsultUpdateRequest,
  RequestUpdateSessionNotesRequest,
  ResponseConsultsConsultWithShortNotesResponse,
  ResponseConsultsNoteAttachmentFile,
} from "@src/api/v1/data-contracts";
import { Consults } from "@src/api/v1/Consults";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";
import { TranscriptionSummaryResponse } from "@src/models/Consults/ConsultsTranscriptSummaryResponse";

export const getConsultById = (clientId: string, consultId: number) =>
  new Clients(OpenUpHttpClient).getClientConsultWithNotes(consultId, clientId);

export const useClientConsultsQuery = (clientId: string | undefined) =>
  useQuery({
    queryKey: ["client-consults", clientId],
    queryFn: () => new Clients(OpenUpHttpClient).getClientConsults(clientId!),
    enabled: !!clientId,
    suspense: false,
  }) as QueryObserverSuccessResult<
    ResponseConsultsConsultWithShortNotesResponse[]
  >;

export const useConsultQuery = (clientId: string, consultId: number) => {
  return useQuery({
    queryKey: ["client-consults", clientId, consultId],
    queryFn: () => getConsultById(clientId, consultId),
    enabled: !!(clientId && consultId),
  });
};

// FLAGS: silent-failure
// `.catch(() => null)` swallows every failure, not just the 404 it is there for: a 403 or 500 resolves as "no upcoming
// session", so React Query records a success and nothing but the App Insights exception marks it.
export const useGetClosestClientConsult = (clientId: string | undefined) =>
  useQuery({
    queryKey: ["client-consults", clientId, "closest-consult"],
    queryFn: () =>
      new Clients(OpenUpHttpClient)
        .getClosestClientConsult(clientId!)
        .catch(() => null),
    enabled: !!clientId,
  });

// FLAGS: authorization
// The clientId parameter never reaches the server: the request is keyed on consultId alone and clientId is
// used only to build the invalidateQueries key below. The signature reads as scoped and is not, so the whole
// ownership boundary for a note write is whatever the API policy enforces.
export const useConsultNotesMutation = (
  clientId: string,
  consultId: number,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: RequestUpdateSessionNotesRequest) =>
      new Consults(OpenUpHttpClient).addOUpdateSessionNote(consultId, value),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["client-consults", clientId, consultId],
      });
    },
  });
};

// FLAGS: health-data, irreversible
// Deletes session-note entries by key; nothing here keeps a copy and the API returns no undo handle.
export const useDeleteSessionNoteMutation = (
  clientId: string,
  consultId: number,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: string[]) =>
      new Consults(OpenUpHttpClient).deleteSessionNote(consultId, value),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["client-consults", clientId, consultId],
      });
    },
  });
};

export const useConsultMutation = (clientId: string) => {
  const queryClient = useQueryClient();
  const onSettled = () => {
    queryClient.invalidateQueries({
      queryKey: ["client-consults", clientId],
    });
    queryClient.invalidateQueries({
      queryKey: [queryIdentifiers.psychologistManagement],
    });
    queryClient.invalidateQueries({
      queryKey: [queryIdentifiers.consults],
    });
  };

  const createOrUpdate = useMutation({
    mutationFn: (
      consult: { id: number } & (
        | RequestConsultUpdateRequest
        | RequestConsultCreateRequest
      ),
    ) =>
      consult.id
        ? new Clients(OpenUpHttpClient).updateConsult(
            consult.id,
            clientId,
            consult as RequestConsultUpdateRequest,
          )
        : new Clients(OpenUpHttpClient).createConsult(
            clientId,
            consult as RequestConsultCreateRequest,
          ),
    onSettled,
  });

  const updateConsult = useMutation({
    mutationFn: (consult: { id: number } & RequestConsultUpdateRequest) =>
      new Clients(OpenUpHttpClient).updateConsult(
        consult.id,
        clientId,
        consult,
      ),
    onSettled,
  });
  const updateConsultStatus = useMutation({
    mutationFn: (consult: Consult) =>
      new Consults(OpenUpHttpClient).updateConsultStatus(
        consult.id,
        consult.status,
      ),
    onSettled,
  });

  const updateConsultTheme = useMutation({
    mutationFn: (consult: Pick<Consult, "id" | "themeKey">) =>
      new Clients(OpenUpHttpClient).updateConsultTheme(
        consult.id,
        consult.themeKey,
        clientId,
      ),
    onSettled,
  });

  const createConsult = useMutation({
    mutationFn: (consult: RequestConsultCreateRequest) =>
      new Clients(OpenUpHttpClient).createConsult(clientId, consult),
    onSettled,
  });

  return {
    createOrUpdate,
    updateConsult,
    createConsult,
    updateConsultStatus,
    updateConsultTheme,
  };
};

// FLAGS: health-data, silent-failure
// Maps 404 to `[]` and every other failure to `null`, then casts the result to a success holding an array — a caller
// that trusts the cast reads `null.length` on the first 403 or 500.
export const useSessionNoteAttachmentsQuery = (consultId: number) =>
  useQuery({
    queryKey: ["consults", consultId, "attachments"],
    queryFn: async () =>
      new Consults(OpenUpHttpClient)
        .getSessionNoteAttachments(consultId)
        .catch((error) => (error.response.status === 404 ? [] : null)),
    suspense: false,
    enabled: !!consultId,
  }) as QueryObserverSuccessResult<ResponseConsultsNoteAttachmentFile[]>;

export const useUploadSessionNoteAttachmentMutation = (consultId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) =>
      new Consults(OpenUpHttpClient).uploadSessionNoteAttachment(
        consultId,
        formData,
        {
          headers: {
            "Content-Type": `multipart/form-data`,
          },
        },
      ),

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["consults", consultId, "attachments"],
      });
    },
  });
};
// FLAGS: health-data, irreversible
// Deletes an uploaded attachment from a session note by file name; the blob is gone once the API accepts.
export const useDeleteSessionNoteAttachmentMutation = (consultId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileName: string) =>
      new Consults(OpenUpHttpClient).deleteSessionNoteAttachment(
        consultId,
        fileName,
      ),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["consults", consultId, "attachments"],
      });
    },
  });
};

export const connectTranscription = async (
  consultId: number,
  serverCallId: string,
) =>
  new Consults(OpenUpHttpClient).connectTranscription({
    consultId,
    serverCallId,
  });

// FLAGS: health-data
// Returns the AI summary of a recorded therapy session, re-typed through `as unknown as` — the generated contract and
// `TranscriptionSummaryResponse` are not checked against each other, so a backend field rename fails only at runtime.
export const getTranscriptionSummary = async (consultId: number) =>
  new Consults(OpenUpHttpClient).getTranscriptionSummary(
    consultId,
  ) as unknown as TranscriptionSummaryResponse;
